// MACHINERY V2 (Stroje) — the status-filter vocabulary (M2.2).
//
// ONE table, consumed by BOTH filter surfaces: the LiveFleetStrip counters at
// the top of the fleet and the "Stav" section of the FilterRail. The artboard
// draws both (machinery-live.jsx LiveFleetStrip + machinery-shared.jsx
// StatusFilterRow), and they must offer the SAME set in the SAME order or a
// state becomes filterable in one place and invisible in the other.
//
// ONE CELL = ONE CANONICAL STATE. The artboard's strip overlaps its cells (its
// "zastaralé dáta" counter also counts machines already counted under alarm),
// which is fine for a static mock but wrong for a toggle: an overlapping filter
// makes "alarm + stale" ambiguous. So every entry below is exactly one of the
// nine states, and `stale-alarm` gets its own cell rather than being smeared
// across two.
//
// COLOUR + ICON come from STATUS_SPECS wherever the state maps onto one of the
// five statuses, so the filter row can never disagree with the badge it filters
// for. The three states that are NOT a bare status (`unconnected`, `awaiting`,
// `stale-alarm`) declare their own pairing here, matching what the card paints.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import {
  MachineryV2CardState,
  MachineryV2IconKind,
  MachineryV2StatusToken,
  STATUS_SPECS,
} from "./cardStates";

export interface FleetStateFilterSpec {
  state: MachineryV2CardState;
  /** i18next key, myMachinery namespace. */
  labelKey: string;
  /** null = no icon and no colour: the "nothing is reported" states. */
  icon: MachineryV2IconKind | null;
  token: MachineryV2StatusToken | null;
}

/** Urgency-first, exactly as the artboard's strip leads with alarms. */
export const FLEET_STATE_FILTERS: readonly FleetStateFilterSpec[] = [
  {
    state: "alarm",
    labelKey: "myMachinery:v2.states.alarm",
    icon: STATUS_SPECS.alarm.icon,
    token: STATUS_SPECS.alarm.token,
  },
  {
    state: "stale-alarm",
    labelKey: "myMachinery:v2.states.staleAlarm",
    icon: STATUS_SPECS.alarm.icon,
    token: STATUS_SPECS.alarm.token,
  },
  {
    state: "running",
    labelKey: "myMachinery:v2.states.running",
    icon: STATUS_SPECS.running.icon,
    token: STATUS_SPECS.running.token,
  },
  {
    state: "idle",
    labelKey: "myMachinery:v2.states.idle",
    icon: STATUS_SPECS.idle.icon,
    token: STATUS_SPECS.idle.token,
  },
  {
    state: "stopped",
    labelKey: "myMachinery:v2.states.stopped",
    icon: STATUS_SPECS.stopped.icon,
    token: STATUS_SPECS.stopped.token,
  },
  {
    state: "stale",
    labelKey: "myMachinery:v2.states.stale",
    icon: null,
    token: "--amber",
  },
  {
    state: "offline",
    labelKey: "myMachinery:v2.states.offline",
    icon: STATUS_SPECS.offline.icon,
    token: STATUS_SPECS.offline.token,
  },
  {
    state: "awaiting",
    labelKey: "myMachinery:v2.states.awaiting",
    icon: "hourglass",
    token: null,
  },
  {
    state: "unconnected",
    labelKey: "myMachinery:v2.states.unconnected",
    icon: null,
    token: null,
  },
];

/** Tinted ink for a filter row/cell. Mirrors the artboard's
 *  `color-mix(in oklab, var(--token) 82%, var(--ink))`. */
export const filterInk = (token: MachineryV2StatusToken | null): string =>
  token ? "color-mix(in oklab, var(" + token + ") 82%, var(--ink))" : "var(--ink-2)";

/** The states that mean "the gateway is telling us something" — the strip's
 *  "N connected" readout. `unconnected` is the only state that is NOT a
 *  connection. */
export const isConnectedState = (state: MachineryV2CardState): boolean =>
  state !== "unconnected";
